import { FC, useMemo, useState } from 'react';
import { Stack } from '@mui/material';
import { Link } from '@tanstack/react-router';
import Text from '../text';
import AccordionButton from '../accordion-button';
import { McpTool } from '@/lib/models/user-event';
import ControlChip from '../chip/control-chip';
import Chip from '../chip';
import ClassificationActionChip from '../chip/classification-action-chip';
import { getMcpToolById } from '@/lib/services/mcp.service';
import FlatButton from '../flat-button';
import { useQuery } from 'react-query';
import { QueryKey } from '@/lib/query-client';
import NoneChip from '../chip/none-chip';
import ReadonlyView from '../form/readonly-view';
import McpServerIcon from '../mcp-server-icon';
import Tooltip from '../tooltip';
import { McpServerModel } from '@/lib/models/mcp-server.model';

interface Props {
  tool: McpTool;
  mcpServer?: McpServerModel;
}

const ReadOnlyFields = [
  { label: 'Description', prop: 'description' },
  { label: 'Server', prop: 'serverName' },
  { label: 'Classification', prop: 'classification' },
];

type ReadonlyDataArgs = {
  tool: McpTool;
  serverIcon: string;
  displayServerName: string;
  displayDescription: string;
  displayShowMore: boolean;
  handleShowMore: () => void;
};

const getReadonlyData = ({
  tool,
  serverIcon,
  displayServerName,
  displayDescription,
  displayShowMore,
  handleShowMore,
}: ReadonlyDataArgs) => {
  const { serverId, classification } = tool;
  const hasDescription = !!displayDescription;
  const descriptionEl = hasDescription ? (
    <Stack direction="row" gap={1} alignItems="center">
      <Text size="small">
        {displayDescription}
        {displayShowMore ? '...' : ''}
      </Text>
      {displayShowMore && (
        <FlatButton
          label="Show more"
          onClick={handleShowMore}
          color="link"
          size="small"
          dotdot={false}
        />
      )}
    </Stack>
  ) : (
    <NoneChip />
  );

  const serverEl = serverId ? (
    <Stack direction="row" gap={1} alignItems="center">
      <McpServerIcon icon={serverIcon} serverName={displayServerName} size="small" />
      <Link to="/config/mcp-servers" search={{ drawer: 'mcp-server', dp_id: serverId }}>
        <Text size="small" color="link" bold>
          {displayServerName || 'View Server'}
        </Text>
      </Link>
    </Stack>
  ) : (
    <NoneChip notAvailable />
  );

  return {
    description: descriptionEl,
    serverName: serverEl,
    classification: <ClassificationActionChip value={classification.action} size="small" />,
  };
};

const ToolServiceButton: FC<Props> = ({ tool, mcpServer }) => {
  const { id, name, blocked, blockAction, description, execution, serverName } = tool;
  const [showMore, setShowMore] = useState(false);

  const controlValue = useMemo(() => {
    if (!blocked || blockAction === 'ignore') {
      return 'allow';
    }
    return blockAction;
  }, [blocked, blockAction]);

  const query = useQuery([QueryKey.McpToolView, id], async () => getMcpToolById(id), {
    enabled: showMore,
  });
  const { data } = query;

  const firstSentence = (description || '').split('.')[0].trim() || '';
  const isTruncated = description?.endsWith('...');
  const displayDescription = data?.description || firstSentence;

  const displayShowMore = isTruncated && !showMore;

  const serverIcon = mcpServer?.icon || '';
  const displayServerName = mcpServer?.title || serverName;
  const hasServerName = !!displayServerName;

  const handleShowMore = () => {
    setShowMore(true);
  };

  const TitleEl = (
    <Stack direction="row" gap={1} alignItems="center" width="100%">
      <Tooltip title={displayServerName} disabled={!hasServerName}>
        <McpServerIcon icon={serverIcon} serverName={displayServerName} size="small" />
      </Tooltip>
      <Text>{name}</Text>

      <Stack flexDirection="row" justifyContent="flex-end" alignItems="center" flexGrow={1} gap={1}>
        {execution.detected && <Chip label="EXECUTED" color="info" size="small" />}

        <ControlChip value={controlValue} size="small" />
      </Stack>
    </Stack>
  );

  return (
    <AccordionButton title={TitleEl}>
      <ReadonlyView
        fields={ReadOnlyFields}
        data={getReadonlyData({
          tool,
          serverIcon,
          displayServerName,
          displayDescription,
          displayShowMore,
          handleShowMore,
        })}
      />
    </AccordionButton>
  );
};

export default ToolServiceButton;
